import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Animal, IntakeType, Gender, Species } from '@rehome/shared';
import {
  generateIntakeDescription, createAnimal, upsertAnimalIntakeRecord, attachIntakePhotos, toast,
  canonicalizeTraits, AddressAutocompleteInput,
  vaccinationsForSpecies, ageFromBirthDate, HEARTWORM_LABELS, StaticMap } from '@rehome/shared';
import type { VaccinationMap, HeartwormResult, TriState } from '@rehome/shared';
import { MapPin, ScanLine, Save, Loader2, Cpu, CalendarClock, ChevronDown, ChevronUp, PawPrint, ImageIcon } from 'lucide-react';
import { SpeciesBreedSelect } from '../components/SpeciesBreedSelect';
import { PhotoUploadField, type PendingPhoto } from '../components/PhotoUploadField';

// ─── IntakeFormPage ────────────────────────────────────────────────────────
// 4-step intake wizard: Helyszín & körülmények → Chip → Faj/Fajta/Alapadatok → Fotók
// From design_plan IntakeForm.tsx
// ===========================================================================

const inputCls = 'w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 bg-white';
const labelCls = 'block text-sm font-medium text-gray-700 mb-2';
// Optional-field labels: no "(opcionális)" suffix — just a faded label, per
// the fields that are genuinely optional (finder contact, reference number).
const labelMutedCls = 'block text-sm font-medium text-gray-700 mb-2 opacity-[0.85]';

/** Tri-state (nem ismert / igen / nem) helper for health flags — DB column is a nullable boolean. */
const triStateToValue = (v: boolean | null | undefined): string => (v === true ? 'yes' : v === false ? 'no' : '');
const valueToTriState = (v: string): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);

const IntakeFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { shelterId } = useParams<{ shelterId: string }>();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [intakeData, setIntakeData] = useState<Partial<Animal>>({
    intakeType: 'Stray',
    foundLocation: { lat: 0, lng: 0, address: '', foundDate: new Date().toISOString().split('T')[0] },
    name: '', species: 'Kutya', breed: '', gender: 'Kan', birthDate: null, weightKg: null, color: null,
    isVaccinated: null, isNeutered: null, isDewormed: null,
    vaccinations: {}, heartwormTest: null, heartwormTestedAt: null,
    traits: [], bio: '', status: 'StrayHold',
  });

  // Staff-only intake details (animal_intake_records — never public, see 028_intake_refinement.sql)
  const [intakeExtras, setIntakeExtras] = useState({
    circumstances: '', chipOwnerInfo: '', intakeReferenceNumber: '', finderName: '', finderPhone: '',
  });

  const [showGpsAdvanced, setShowGpsAdvanced] = useState(false);

  // ─── Step validation — blocks advancing/saving until required fields are filled ───
  const validateLocation = (): string | null => {
    const hasAddress = !!intakeData.foundLocation?.address?.trim();
    const hasCoords = !!(intakeData.foundLocation?.lat && intakeData.foundLocation?.lng);
    if (!hasAddress && !hasCoords) return 'Add meg a megtalálás helyét: legalább egy cím vagy GPS koordináták szükségesek.';
    return null;
  };
  const validateBasicInfo = (): string | null => {
    if (!intakeData.name?.trim()) return 'Add meg az állat nevét.';
    if (!intakeData.breed?.trim()) return 'Válaszd ki (vagy add meg) a fajtát.';
    if (!intakeData.gender) return 'Add meg az állat nemét.';
    return null;
  };

  const goToStep2 = () => {
    const err = validateLocation();
    if (err) { toast.error(err); return; }
    setStep(2);
  };
  const goToStep4 = () => {
    const err = validateBasicInfo();
    if (err) { toast.error(err); return; }
    setStep(4);
  };

  // Step 1: Location — GPS is an optional shortcut, not the only way in.
  // It only prefills the address when the staff member hasn't already typed
  // one; it never overwrites a manually entered address.
  const handleGetLocation = () => {
    setLoading(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(pos => {
        setIntakeData(prev => {
          const coordsText = `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`;
          return {
            ...prev,
            foundLocation: {
              ...prev.foundLocation!,
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              address: prev.foundLocation?.address ? prev.foundLocation.address : coordsText,
            },
          };
        });
        setLoading(false);
      }, () => { toast.error('Nem sikerült lekérni a pozíciót.'); setLoading(false); });
    } else { toast.error('A böngésző nem támogatja a helymeghatározást.'); setLoading(false); }
  };

  // Step 2: Chip
  const [chipQuery, setChipQuery] = useState('');
  const [chipResult, setChipResult] = useState<string | null>(null);

  const handleChipLookup = () => {
    setLoading(true);
    setTimeout(() => {
      if (chipQuery.length === 15) {
        setIntakeData(prev => ({ ...prev, microchipId: chipQuery, chipProvider: 'Petlog', chipStatus: 'Registered' }));
        setIntakeExtras(prev => ({ ...prev, chipOwnerInfo: 'Ismeretlen (Adatvédett)' }));
        setChipResult('Sikeres azonosítás! Regisztrált chip: Petlog adatbázis.');
      } else {
        setIntakeData(prev => ({ ...prev, microchipId: chipQuery, chipProvider: 'Unknown', chipStatus: 'Unregistered' }));
        setChipResult('A chip nem található a nemzetközi adatbázisokban.');
      }
      setLoading(false);
    }, 1500);
  };

  // Step 3: AI description — combines the structured fields already entered
  // (species/breed/color/health flags/circumstances) with free-text staff
  // observations (temperament, injuries, condition) into an editable bio.
  // Does NOT re-guess species/breed/age — those come from SpeciesBreedSelect.
  const [observations, setObservations] = useState('');
  const [generatingBio, setGeneratingBio] = useState(false);

  const handleGenerateDescription = async () => {
    setGeneratingBio(true);
    const result = await generateIntakeDescription({
      name: intakeData.name || 'Névtelen',
      species: intakeData.species || 'Kutya',
      breed: intakeData.breed || 'Keverék',
      gender: intakeData.gender,
      age: intakeData.age,
      color: intakeData.color,
      isVaccinated: intakeData.isVaccinated,
      isNeutered: intakeData.isNeutered,
      isDewormed: intakeData.isDewormed,
      circumstances: intakeExtras.circumstances,
      observations,
    });
    // A bio szó szerint marad, ahogy az AI megírta — csak a kulcsszavakat
    // igazítjuk a közös szótárhoz, hogy a szűrőben ne szaporodjanak a rokon
    // értelmű chipek. A halk (notify:false) mód szándékos: itt a staff még
    // nem is látta a javasolt kulcsszavakat, egy "átneveztük" toast csak
    // zavaró lenne — a végeredmény ott van a képernyőn.
    const traits = await canonicalizeTraits(result.traits, { notify: false });
    setIntakeData(prev => ({ ...prev, bio: result.bio, traits }));
    setGeneratingBio(false);
  };

  // Step 4: Photos
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);

  // Step 3/4 shared: Submit
  // Whether the animal is publicly adoptable right away. Irrelevant for Stray
  // intakes — those are always legally held (see calculateHoldDate) and can
  // never be marked adoptable at intake, regardless of this checkbox.
  const [isAdoptable, setIsAdoptable] = useState(true);

  const calculateHoldDate = () => {
    const today = new Date(); today.setDate(today.getDate() + 14); return today.toISOString().split('T')[0];
  };

  const handleSubmit = async () => {
    const locationErr = validateLocation();
    if (locationErr) { toast.error(locationErr); return; }
    const basicInfoErr = validateBasicInfo();
    if (basicInfoErr) { toast.error(basicInfoErr); return; }
    if (photos.length === 0) {
      toast.error('Legalább 1 fotó feltöltése kötelező.');
      return;
    }

    setLoading(true);

    // Az azonosítót előre generáljuk, de a fotók csak a sor létrejötte UTÁN
    // tölthetők fel — l. attachIntakePhotos.
    const id = crypto.randomUUID();

    // Stray → always the legally-mandated hold, never overridable.
    // Non-Stray → staff decides via the "Örökbefogadható" checkbox: checked
    // lists the animal immediately, unchecked parks it in Medical (assessment
    // gate) until staff flips it to Available on the profile page later.
    const status = intakeData.intakeType === 'Stray' ? 'StrayHold' : (isAdoptable ? 'Available' : 'Medical');

    const result = await createAnimal({
      id,
      shelterId: shelterId || '',
      name: intakeData.name || 'Névtelen',
      species: intakeData.species as Species,
      breed: intakeData.breed || 'Keverék',
      age: intakeData.age || 0,
      gender: intakeData.gender as Gender,
      status,
      traits: intakeData.traits || [],
      bio: intakeData.bio || 'Frissen bekerült állat.',
      intakeType: intakeData.intakeType as IntakeType,
      intakeDate: new Date().toISOString().split('T')[0],
      foundLocation: intakeData.foundLocation || null,
      strayHoldEndDate: intakeData.intakeType === 'Stray' ? calculateHoldDate() : null,
      microchipId: intakeData.microchipId || null,
      chipProvider: intakeData.chipProvider || null,
      chipStatus: intakeData.chipStatus || null,
      weightKg: intakeData.weightKg ?? null,
      color: intakeData.color || null,
      isVaccinated: intakeData.isVaccinated ?? null,
      isNeutered: intakeData.isNeutered ?? null,
      isDewormed: intakeData.isDewormed ?? null,
      birthDate: intakeData.birthDate ?? null,
      vaccinations: intakeData.vaccinations ?? {},
      heartwormTest: intakeData.heartwormTest ?? null,
      heartwormTestedAt: intakeData.heartwormTestedAt ?? null,
      imageUrl: null,
      gallery: [],
      privateGallery: [],
    });

    if (result) {
      // A fotók feltöltése és hozzárendelése — a privát tároló szabálya
      // létező állatot vár, ezért csak itt futhat.
      await attachIntakePhotos(id, photos.map(p => ({ dataUrl: p.dataUrl, isPublic: p.isPublic })));

      const hasIntakeExtras = intakeExtras.circumstances.trim() || intakeExtras.chipOwnerInfo.trim()
        || intakeExtras.intakeReferenceNumber.trim() || intakeExtras.finderName.trim() || intakeExtras.finderPhone.trim();
      if (hasIntakeExtras) {
        await upsertAnimalIntakeRecord(result.id, {
          circumstances: intakeExtras.circumstances.trim() || null,
          chipOwnerInfo: intakeExtras.chipOwnerInfo.trim() || null,
          intakeReferenceNumber: intakeExtras.intakeReferenceNumber.trim() || null,
          finderName: intakeExtras.finderName.trim() || null,
          finderPhone: intakeExtras.finderPhone.trim() || null,
        });
      }
    }

    setLoading(false);
    if (result) {
      // Back to wherever the intake wizard was opened from — normally the
      // shelter dashboard ("Bekerülés rögzítése" quick action).
      navigate(-1);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-bold text-gray-900">Bekerülés és Azonosítás</h1><p className="text-gray-600">Új állat rögzítése, chip olvasás</p></div>
        <div className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">Lépés {step} / 4</div>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5"><div className="bg-teal-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${(step / 4) * 100}%` }} /></div>

      {/* STEP 1: Location & circumstances */}
      {step === 1 && (
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 space-y-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center"><MapPin className="w-5 h-5 mr-2 text-teal-600" />1. Helyszín és körülmények</h2>

          <div>
            <label className={labelCls}>Bekerülés Típusa</label>
            <select className={inputCls} value={intakeData.intakeType || ''} onChange={e => setIntakeData({ ...intakeData, intakeType: e.target.value as IntakeType })}>
              <option value="Stray">Talált (Kóbor)</option>
              <option value="Surrender">Leadott (Tulajdonosi)</option>
              <option value="Confiscated">Elkobzott (Hatósági)</option>
              <option value="Return">Visszavett (Sikertelen örökbefogadás)</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Cím</label>
            <AddressAutocompleteInput
              value={intakeData.foundLocation?.address || ''}
              onChange={address => setIntakeData(prev => ({ ...prev, foundLocation: { ...prev.foundLocation!, address } }))}
              onPlaceSelected={({ address, lat, lng }) => setIntakeData(prev => ({ ...prev, foundLocation: { ...prev.foundLocation!, address, lat, lng } }))}
            />
            {/* A térkép a visszaellenőrzésé: a cím és a koordináta külön is
                elírható, és a kettő eltérése ránézésre másképp nem tűnik fel. */}
            <StaticMap
              lat={intakeData.foundLocation?.lat}
              lng={intakeData.foundLocation?.lng}
              label={intakeData.foundLocation?.address || null}
              className="mt-2"
            />
          </div>

          {/* GPS coordinates — secondary/rare, tucked away by default */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowGpsAdvanced(prev => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg"
            >
              <span>GPS koordináták</span>
              {showGpsAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showGpsAdvanced && (
              <div className="px-4 pb-4 flex gap-2">
                <input
                  type="number" step="0.0001"
                  value={intakeData.foundLocation?.lat || ''}
                  onChange={e => setIntakeData(prev => ({ ...prev, foundLocation: { ...prev.foundLocation!, lat: Number(e.target.value) } }))}
                  placeholder="Szélesség" className="w-full p-3 border border-gray-300 rounded-lg"
                />
                <input
                  type="number" step="0.0001"
                  value={intakeData.foundLocation?.lng || ''}
                  onChange={e => setIntakeData(prev => ({ ...prev, foundLocation: { ...prev.foundLocation!, lng: Number(e.target.value) } }))}
                  placeholder="Hosszúság" className="w-full p-3 border border-gray-300 rounded-lg"
                />
                <button type="button" onClick={handleGetLocation} disabled={loading} title="Pozíció lekérése automatikusan" className="bg-teal-100 text-teal-700 px-4 rounded-lg hover:bg-teal-200 transition-colors flex items-center flex-shrink-0">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MapPin className="w-5 h-5" />}</button>
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Körülmények leírása</label>
            <textarea
              value={intakeExtras.circumstances}
              onChange={e => setIntakeExtras(prev => ({ ...prev, circumstances: e.target.value }))}
              rows={3}
              className={inputCls}
              placeholder="Pl. udvaron találtuk, megkötözve, sérülés nélkül..."
            />
            <p className="text-xs text-gray-400 mt-1.5">Ez az adat nem publikus — csak a menhelyi staff láthatja.</p>
          </div>

          {intakeData.intakeType === 'Confiscated' && (
            <div>
              <label className={labelMutedCls}>Hatósági iktatószám</label>
              <input
                type="text" value={intakeExtras.intakeReferenceNumber}
                onChange={e => setIntakeExtras(prev => ({ ...prev, intakeReferenceNumber: e.target.value }))}
                className={inputCls} placeholder="pl. jegyzőkönyv/iktatószám"
              />
              <p className="text-xs text-gray-400 mt-1.5">Nem publikus — staff-only adat.</p>
            </div>
          )}

          {intakeData.intakeType === 'Stray' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelMutedCls}>Megtaláló neve</label>
                <input
                  type="text" value={intakeExtras.finderName}
                  onChange={e => setIntakeExtras(prev => ({ ...prev, finderName: e.target.value }))}
                  className={inputCls} placeholder="Ha szeretne visszajelzést kapni"
                />
              </div>
              <div>
                <label className={labelMutedCls}>Megtaláló telefonja</label>
                <input
                  type="tel" value={intakeExtras.finderPhone}
                  onChange={e => setIntakeExtras(prev => ({ ...prev, finderPhone: e.target.value }))}
                  className={inputCls} placeholder="+36 30 123 4567"
                />
              </div>
              <p className="text-xs text-gray-400 -mt-2 sm:col-span-2">Nem publikus — staff-only adat.</p>
            </div>
          )}

          <div className="flex justify-end pt-4"><button onClick={goToStep2} className="w-full sm:w-auto bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors">Tovább az Azonosításhoz</button></div>
        </div>
      )}

      {/* STEP 2: Chip */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 space-y-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center"><ScanLine className="w-5 h-5 mr-2 text-teal-600" />2. Chip és Azonosítás</h2>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1 w-full">
                <label className={labelCls}>Microchip Szám (15 jegy)</label>
                <input type="text" maxLength={15} value={chipQuery} onChange={e => setChipQuery(e.target.value.replace(/\D/g, ''))} className="w-full p-3 border border-gray-300 rounded-lg font-mono" placeholder="Pl. 981000012345678" />
              </div>
              <button onClick={handleChipLookup} disabled={loading || chipQuery.length !== 15} className="w-full md:w-auto bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 transition-all">{loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Chip Ellenőrzése'}</button>
            </div>
            {chipResult && <div className={`p-3 rounded-lg text-sm ${chipResult.includes('Sikeres') ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>{chipResult}</div>}
            {intakeExtras.chipOwnerInfo && (
              <div>
                <label className={labelCls}>Chip tulajdonos info (nem publikus)</label>
                <input
                  type="text" value={intakeExtras.chipOwnerInfo}
                  onChange={e => setIntakeExtras(prev => ({ ...prev, chipOwnerInfo: e.target.value }))}
                  className={inputCls}
                />
              </div>
            )}
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
            <button onClick={() => setStep(1)} className="text-gray-600 px-4 py-2 hover:bg-gray-100 rounded-lg">← Vissza</button>
            <button onClick={() => setStep(3)} className="bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700">Tovább a Faj/Fajta adatokhoz</button>
          </div>
        </div>
      )}

      {/* STEP 3: Species/Breed, basic info & AI description */}
      {step === 3 && (
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 space-y-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center"><PawPrint className="w-5 h-5 mr-2 text-teal-600" />3. Faj, fajta és alapadatok</h2>

          <SpeciesBreedSelect
            species={intakeData.species || 'Kutya'}
            breed={intakeData.breed || ''}
            onSpeciesChange={species => setIntakeData(prev => ({ ...prev, species: species as Species }))}
            onBreedChange={breed => setIntakeData(prev => ({ ...prev, breed }))}
            inputClassName={inputCls}
          />

          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Név</label><input type="text" value={intakeData.name} onChange={e => setIntakeData({ ...intakeData, name: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Nem</label><select value={intakeData.gender || ''} onChange={e => setIntakeData({ ...intakeData, gender: e.target.value as Gender })} className={inputCls}><option value="Kan">Kan</option><option value="Nőstény">Nőstény</option></select></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Becsült születési dátum</label>
              <input
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={intakeData.birthDate ?? ''}
                onChange={e => setIntakeData({ ...intakeData, birthDate: e.target.value || null })}
                className={inputCls}
              />
              {/* A kor ebből SZÁRMAZIK (l. resolveAge a mutations.ts-ben) — a
                  dátum nem avul el, az évszám igen. Itt csak visszajelzés. */}
              <p className="text-[11px] text-gray-400 mt-1">
                {intakeData.birthDate
                  ? `Ez kb. ${ageFromBirthDate(intakeData.birthDate) ?? 0} éves kornak felel meg.`
                  : 'Ha csak a kor becsülhető, add meg a hozzávetőleges születési évet (pl. január 1-jével).'}
              </p>
            </div>
            <div><label className={labelCls}>Becsült súly (kg)</label><input type="number" min="0" step="0.1" value={intakeData.weightKg ?? ''} onChange={e => setIntakeData({ ...intakeData, weightKg: e.target.value ? Number(e.target.value) : null })} className={inputCls} /></div>
          </div>

          <div>
            <label className={labelCls}>Szín / külső jegyek</label>
            <input type="text" value={intakeData.color ?? ''} onChange={e => setIntakeData({ ...intakeData, color: e.target.value })} className={inputCls} placeholder="pl. fekete-fehér, barna foltokkal" />
          </div>

          <div>
            <label className={labelCls}>Egészségi állapot (ha ismert)</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Oltott</label>
                <select value={triStateToValue(intakeData.isVaccinated)} onChange={e => setIntakeData({ ...intakeData, isVaccinated: valueToTriState(e.target.value) })} className={inputCls}>
                  <option value="">Nem ismert</option><option value="yes">Igen</option><option value="no">Nem</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Ivartalanítva</label>
                <select value={triStateToValue(intakeData.isNeutered)} onChange={e => setIntakeData({ ...intakeData, isNeutered: valueToTriState(e.target.value) })} className={inputCls}>
                  <option value="">Nem ismert</option><option value="yes">Igen</option><option value="no">Nem</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Parazitamentes</label>
                <select value={triStateToValue(intakeData.isDewormed)} onChange={e => setIntakeData({ ...intakeData, isDewormed: valueToTriState(e.target.value) })} className={inputCls}>
                  <option value="">Nem ismert</option><option value="yes">Igen</option><option value="no">Nem</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Részletes oltások ──────────────────────────────────────────
              A fenti "Oltott" egyetlen jelölő; itt derül ki, MELYIK oltás van
              meg. A felsorolás fajfüggő (l. VACCINATIONS a shared-ben), és a
              "Nem tudjuk" külön válasz — bekerüléskor ez a leggyakoribb, és
              érdemben más, mint a "Nem". */}
          <div>
            <label className={labelCls}>Oltások részletesen</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {vaccinationsForSpecies(intakeData.species as any).map(v => (
                <div key={v.key}>
                  <label className="block text-xs text-gray-500 mb-1" htmlFor={`vacc-${v.key}`}>
                    {v.label}
                    {v.hint && <span className="text-gray-400"> · {v.hint}</span>}
                  </label>
                  <select
                    id={`vacc-${v.key}`}
                    value={(intakeData.vaccinations as VaccinationMap | undefined)?.[v.key] ?? ''}
                    onChange={e => {
                      const next: VaccinationMap = { ...(intakeData.vaccinations as VaccinationMap | undefined ?? {}) };
                      if (e.target.value) next[v.key] = e.target.value as TriState;
                      else delete next[v.key]; // "nincs adat" = hiányzó kulcs
                      setIntakeData({ ...intakeData, vaccinations: next });
                    }}
                    className={inputCls}
                  >
                    <option value="">Nincs adat</option>
                    <option value="yes">Igen</option>
                    <option value="no">Nem</option>
                    <option value="unknown">Nem tudjuk</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* ── Szívféreg-teszt ────────────────────────────────────────────
              Külön szótár: itt a "pozitív" a ROSSZ hír, nem az "igen". */}
          <div>
            <label className={labelCls}>Szívféreg-teszt</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1" htmlFor="heartworm-result">Eredmény</label>
                <select
                  id="heartworm-result"
                  value={intakeData.heartwormTest ?? ''}
                  onChange={e => {
                    const result = (e.target.value || null) as HeartwormResult | null;
                    // Eredmény nélkül a dátum sem maradhat (DB CHECK).
                    setIntakeData({ ...intakeData, heartwormTest: result, heartwormTestedAt: result ? intakeData.heartwormTestedAt ?? null : null });
                  }}
                  className={inputCls}
                >
                  <option value="">Nem volt teszt</option>
                  {(Object.keys(HEARTWORM_LABELS) as HeartwormResult[]).map(k => (
                    <option key={k} value={k}>{HEARTWORM_LABELS[k]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1" htmlFor="heartworm-date">Teszt dátuma</label>
                <input
                  id="heartworm-date"
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  disabled={!intakeData.heartwormTest}
                  value={intakeData.heartwormTestedAt ?? ''}
                  onChange={e => setIntakeData({ ...intakeData, heartwormTestedAt: e.target.value || null })}
                  className={inputCls}
                />
                {!intakeData.heartwormTest && (
                  <p className="text-[11px] text-gray-400 mt-1">Előbb válassz eredményt.</p>
                )}
              </div>
            </div>
            {intakeData.heartwormTest === 'positive' && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mt-2">
                Pozitív eredmény — az állat kezelést igényel, és ez befolyásolja az örökbeadhatóságát.
              </p>
            )}
          </div>

          {/* Description — two clear steps: first the staff's own manual
              observations (staff-only, never shown to adopters), then the public
              bio, which can be written by hand OR generated by AI from those
              observations plus the structured fields already entered above.
              (Was a confusingly-labelled block where a manual field sat under an
              "AI generálás" heading with an identically-named button.) */}
          <div className="pt-2 border-t border-gray-100 space-y-4">
            <div>
              <h3 className="font-bold text-gray-800">Bemutatkozás</h3>
              <p className="text-sm text-gray-500 mt-1">
                Előbb jegyezd fel a saját megfigyeléseidet, majd írd meg kézzel vagy generáltasd az AI-val a nyilvános bemutatkozót.
              </p>
            </div>

            {/* 1. Manual observations — staff-only, feeds the AI */}
            <div>
              <label className={labelCls}>Megfigyelések (viselkedés, sérülés, állapot)</label>
              <textarea
                value={observations} onChange={e => setObservations(e.target.value)} rows={3} className={inputCls}
                placeholder="Pl. félénk, sovány, seb az oldalán, valószínűleg bántalmazták..."
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Csak a menhely számára — nem jelenik meg az örökbefogadóknak. Az AI ebből és a fent megadott adatokból fogalmaz nyilvános bemutatkozót.
              </p>
            </div>

            {/* 2. Public bio — written by hand or generated from the observations */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <label className="block text-sm font-medium text-gray-700">Nyilvános bemutatkozó (bio)</label>
                <button
                  type="button" onClick={handleGenerateDescription} disabled={generatingBio}
                  className="inline-flex items-center gap-1.5 text-sm bg-purple-100 text-purple-700 px-3 py-1.5 rounded-lg font-medium hover:bg-purple-200 disabled:opacity-50 shrink-0"
                >
                  {generatingBio ? <><Loader2 className="w-4 h-4 animate-spin" /> Generálás...</> : <><Cpu className="w-4 h-4" /> AI generálás</>}
                </button>
              </div>
              <textarea
                value={intakeData.bio || ''} onChange={e => setIntakeData(prev => ({ ...prev, bio: e.target.value }))} rows={4} className={inputCls}
                placeholder="Ide írhatsz kézzel, vagy generálhatsz AI-val a fenti gombbal."
              />
              {(intakeData.traits || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">Az AI által felismert tulajdonságok</p>
                  <div className="flex flex-wrap gap-2">
                    {(intakeData.traits || []).map((t, i) => <span key={i} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">{t}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
            <button onClick={() => setStep(2)} className="text-gray-600 px-4 py-2 hover:bg-gray-100 rounded-lg">← Vissza</button>
            <button onClick={goToStep4} className="bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700">Tovább a Fotókhoz</button>
          </div>
        </div>
      )}

      {/* STEP 4: Photos & summary */}
      {step === 4 && (
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 space-y-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center"><ImageIcon className="w-5 h-5 mr-2 text-teal-600" />4. Fotók és összegzés</h2>

          <PhotoUploadField photos={photos} onChange={setPhotos} required />

          {intakeData.intakeType === 'Stray' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-medium text-amber-800 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Stray Hold (14 nap)</p>
              <p className="text-sm text-amber-700">A jogi várakozási idő lejárta: <span className="font-bold">{calculateHoldDate()}</span></p>
              <p className="text-xs text-amber-600 mt-1">Ezután az állat örökbefogadhatóvá válik, amennyiben a chip tulajdonosa nem jelentkezik.</p>
            </div>
          ) : (
            <label className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-lg p-4 cursor-pointer">
              <input
                type="checkbox"
                checked={isAdoptable}
                onChange={e => setIsAdoptable(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span>
                <span className="block font-medium text-teal-900">Örökbefogadható</span>
                <span className="block text-sm text-teal-700 mt-0.5">
                  {isAdoptable
                    ? 'Mentés után az állat azonnal megjelenik az örökbefogadható állatok listájában.'
                    : 'Az állat egyelőre nem jelenik meg az örökbefogadóknak — „Orvosi kezelés alatt” státuszba kerül, amíg a menhely fel nem méri. Ez a felvétele utáni profilján bármikor módosítható.'}
                </span>
              </span>
            </label>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
            <button onClick={() => setStep(3)} className="text-gray-600 px-4 py-2 hover:bg-gray-100 rounded-lg">← Vissza</button>
            <button onClick={handleSubmit} disabled={loading || photos.length === 0} className="bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Mentés...</> : <><Save className="w-5 h-5" /> Állat Mentése</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntakeFormPage;
